import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle, Clock, X } from 'lucide-react';
import type { MissedDepositRow } from '@/domain/missedDeposits';
import UnclosedLinkBody from '@/components/ops/UnclosedLinkBody';
import { useSimulationStore } from '@/state/simulationStore';
import { currentActorId } from '@/domain/actor';
import { useBrainStore } from '@/state/brainStore';
import ResendLinkAction from '@/components/deposits/ResendLinkAction';

/**
 * The sweeper's "missed deposits" morning list (bd tv-d2f, board 08): unpaid
 * gap-fill links past their 24h lock, the sweeper's first priority each morning.
 * Links unpaid past 48h are flagged as escalated to an Ops report. Chasing these
 * takes priority over fresh sweeping — the client already said yes to a slot.
 */
export default function MissedDepositsList({ rows: allRows }: { rows: MissedDepositRow[] }) {
  const navigate = useNavigate();
  const clockIso = useSimulationStore((s) => s.clockIso);
  const deposits = useBrainStore((s) => s.deposits);
  // W5 mg: the row being closed off, and the ones closed this visit (hidden at
  // once; the live deposits view drops them on its next refresh).
  const [closing, setClosing] = useState<MissedDepositRow | null>(null);
  const [closedIds, setClosedIds] = useState<ReadonlySet<string>>(new Set());
  const rows = allRows.filter((r) => !closedIds.has(r.depositId));

  if (rows.length === 0) {
    return (
      <div
        className="panel-dark p-6 text-center font-serif text-cream-soft max-w-2xl"
        data-testid="missed-deposits-empty"
      >
        No missed deposits. Every gap-fill link sent is either paid or still inside its 24h lock.
      </div>
    );
  }

  return (
    <div data-testid="missed-deposits-list">
      <p className="font-serif text-[13px] text-cream-soft mb-4 max-w-3xl leading-relaxed">
        Unpaid gap-fill links past their 24h lock — chase these first each morning; the client
        already said yes to a slot. Anything still unclosed past 48h is escalated to an Ops report.
      </p>
      <ul>
        {rows.map((row) => (
          <li
            key={row.depositId}
            className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
            data-testid={`missed-deposit-${row.depositId}`}
          >
            <div
              className={clsxTone(row.escalated)}
              aria-hidden
            >
              {row.escalated ? <AlertTriangle size={17} /> : <Clock size={17} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-inter text-[13.5px] text-cream font-semibold">{row.customerName}</p>
              <p className="font-serif text-[12.5px] text-cream-soft mt-0.5">
                ${row.amountAud.toLocaleString()} link sent {row.hoursSinceSent}h ago, unpaid
                {row.sessionIso ? ` · ${format(new Date(row.sessionIso), 'EEE d MMM')} slot` : ''}
              </p>
              {row.escalated ? (
                <span className="inline-block mt-1.5 rounded-full border border-blood/40 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-blood">
                  Escalated to Ops · 48h+ unclosed
                </span>
              ) : null}
            </div>
            {/* W5b #2: resend straight from the list — the Sweeper sent it, so the
                fresh link stays the Sweeper's (Q3). A gone slot opens "Pick a new date". */}
            {deposits[row.depositId] ? (
              <ResendLinkAction
                deposit={deposits[row.depositId]!}
                sentByRole="sweeper"
                testId={`missed-deposit-resend-${row.depositId}`}
                onResent={() => setClosedIds((prev) => new Set(prev).add(row.depositId))}
              />
            ) : null}
            <button
              type="button"
              onClick={() => setClosing(row)}
              className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-2 text-xs font-semibold text-cream-soft transition hover:border-gold-bright/60 hover:text-gold-bright"
              data-testid={`missed-deposit-close-${row.depositId}`}
            >
              Close off
            </button>
            <button
              type="button"
              onClick={() => (row.customer ? navigate(`/customer/${row.customer.id}`) : navigate('/deposits'))}
              className="shrink-0 rounded-md bg-gold-bright px-4 py-2 text-xs font-semibold text-ink-black transition hover:bg-gold"
              data-testid={`missed-deposit-call-${row.depositId}`}
            >
              Call back
            </button>
          </li>
        ))}
      </ul>
      {closing ? (
        <CloseOffDialog
          row={closing}
          nowIso={clockIso}
          onClose={() => setClosing(null)}
          onDone={() => {
            setClosedIds((prev) => new Set(prev).add(closing.depositId));
            setClosing(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** The Sweeper's close-off: the same body as the Ops "Links >48h" modal, minus "message the sweeper". */
function CloseOffDialog({
  row,
  nowIso,
  onClose,
  onDone,
}: {
  row: MissedDepositRow;
  nowIso: string;
  onClose: () => void;
  onDone: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-cream/10 backdrop-blur-sm"
      onClick={onClose}
      data-testid="missed-closeoff-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Close off ${row.customerName}'s link`}
        className="panel bg-ink-deep max-w-[560px] w-full max-h-[88vh] overflow-y-auto rounded-xl shadow-gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-gold-dim/20 px-6 py-4">
          <div className="min-w-0">
            <p className="label-small-caps text-gold-bright">Missed deposit</p>
            <h2 className="heading-display text-2xl text-cream truncate mt-1">{row.customerName}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-quiet" aria-label="Close" data-testid="missed-closeoff-dismiss">
            <X size={14} />
          </button>
        </header>
        <div className="px-6 py-5">
          <UnclosedLinkBody
            link={row.link}
            customerName={row.customerName}
            by={currentActorId() ?? 'georgina'}
            nowIso={nowIso}
            showMessage={false}
            onDone={onDone}
          />
        </div>
      </div>
    </div>
  );
}

function clsxTone(escalated: boolean): string {
  return [
    'shrink-0 w-9 h-9 rounded-full grid place-items-center',
    escalated ? 'bg-blood/10 text-blood' : 'bg-gold/15 text-gold-bright',
  ].join(' ');
}
